/**
 * <ChampionAvatar> — la tête de l'un de ceux qui relaient le tuteur, « côté ado » (atom).
 *
 * Livre V16, « Un nœud, une place, quatre tailles » : sa tête dans un disque, et un nœud papillon
 * À SA COULEUR, à la place et à la proportion du nœud jaune du tuteur. C'est la parenté entre
 * l'humain et l'agent : même disque, même liseré, même nœud (`SignatureBow`, partagé avec la
 * capsule), seule la couleur du nœud dit qui est qui.
 *
 * Un atome frère d'`<Avatar>`, pas une de ses variantes : `<Avatar>` dessine une PERSONNE (photo
 * ou initiales, sélection), un champion est un dessin de l'app choisi par son nom. Et la capsule
 * du tuteur reste jaune pour tous : la couleur d'un champion n'y revient pas par un branchement.
 *
 * Sa couleur est un costume : elle peint son nœud, rien d'autre. Le ton foncé du pli n'a pas de
 * jeton ; c'est sa propre couleur sous un voile de `text-on-accent`, l'encre des aplats clairs,
 * marine dans tous les thèmes.
 *
 * Un champion n'apparaît que là où il agit : jamais sur un écran vide, une erreur, une boîte de
 * dialogue.
 */
import React from "react"
import { Image, View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import abouHead from "../../../../assets/champions/abou_avatar.png"
import samHead from "../../../../assets/champions/sam_avatar.png"
import tinaHead from "../../../../assets/champions/tina_avatar.png"
import zoeHead from "../../../../assets/champions/zoe_avatar.png"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { dimming } from "../../theme/tokens/effects"
import { TUTOR_HALO } from "./Avatar"
import { SignatureBow } from "./SignatureBow"

/**
 * Les quatre. Le seul type du nom d'un champion dans l'espace élève : une quête, un relais, une
 * bulle le lisent ici. `<Mascot>` (corps entier, atome du parent) garde le sien, sans Abou.
 */
export type ChampionName = "sam" | "zoe" | "tina" | "abou"

type Champion = {
    /** Son prénom, tel qu'il s'écrit. */
    label: string
    /** Sa tête : l'avatar du kit de marque (256 px), embarqué. */
    head: number
    /** Son costume : la couleur de son nœud. */
    color: SemanticColorKey
}

export const CHAMPIONS: Record<ChampionName, Champion> = {
    sam: { label: "Sam", head: samHead, color: "champion-sam" },
    zoe: { label: "Zoé", head: zoeHead, color: "champion-zoe" },
    tina: { label: "Tina", head: tinaHead, color: "champion-tina" },
    abou: { label: "Abou", head: abouHead, color: "champion-abou" },
}

/** Force du voile qui fait le ton foncé du nœud (le pli en prend un peu plus de la moitié). */
const BOW_VEIL = 0.4

export type ChampionAvatarProps = {
    name: ChampionName
    /** Diamètre en px — défaut 44. Les tailles du livre : 72 · 54 · 44 · 34. */
    size?: number
    /** Estompé, nœud compris, comme la capsule d'une séance où l'élève ne sera pas. Défaut `false`. */
    dimmed?: boolean
    testID?: string
}

export function ChampionAvatar(props: ChampionAvatarProps) {
    const { name, size = 44, dimmed = false, testID } = props
    const { theme } = useUnistyles()
    const { label, head, color } = CHAMPIONS[name]

    return (
        <View
            testID={testID}
            accessibilityRole="image"
            accessibilityLabel={label}
            style={[styles.box(size), dimmed ? styles.dimmed : undefined]}
        >
            <View style={styles.disc(size)}>
                <Image source={head} style={styles.head(size - 2 * TUTOR_HALO)} />
            </View>
            <SignatureBow
                size={size}
                color={theme.colors[color]}
                shade={theme.colors["text-on-accent"]}
                shadeOpacity={BOW_VEIL}
            />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    box: (size: number) => ({
        width: size,
        height: size,
    }),
    /** Le disque de la capsule sans photo : même aplat, même liseré blanc. */
    disc: (size: number) => ({
        width: size,
        height: size,
        borderRadius: size / 2,
        borderWidth: TUTOR_HALO,
        borderColor: theme.colors.white,
        backgroundColor: theme.colors["initials-fill"],
        overflow: "hidden" as const,
    }),
    head: (size: number) => ({
        width: size,
        height: size,
    }),
    dimmed: {
        opacity: dimming.opacity,
    },
}))
